/**
 * Validation Strategy Implementations
 * Handle four validation modes: free, blocked, indicators, auto-save
 */

import type { IValidationStrategy } from '../types/index';

/**
 * Free Navigation Validation Strategy
 * Allow switching between forms without validation
 * Validate all forms on submit
 */
export class FreeNavigationValidationStrategy implements IValidationStrategy {
  async canSwitchForm(): Promise<boolean> {
    return true; // Always allow
  }

  async validateBeforeSave(forms: Record<string, any>): Promise<any[]> {
    const errors: any[] = [];
    // Validate all forms on save
    Object.entries(forms).forEach(([formID, formData]) => {
      const formErrors = this.validateForm(formData);
      if (formErrors.length > 0) {
        errors.push({ formID, errors: formErrors });
      }
    });
    return errors;
  }

  onValidationError(errors: any[]): void {
    console.warn('Validation errors on save:', errors);
  }

  private validateForm(formData: any): any[] {
    // Basic validation: check for required fields
    // In real implementation, would call form's validation
    return [];
  }
}

/**
 * Blocked Navigation Validation Strategy
 * Block switching to next form if current is invalid
 */
export class BlockedNavigationValidationStrategy implements IValidationStrategy {
  private currentFormData: any = {};

  async canSwitchForm(formData: any): Promise<boolean> {
    const errors = await this.validateForm(formData);
    return errors.length === 0;
  }

  async validateBeforeSave(forms: Record<string, any>): Promise<any[]> {
    const errors: any[] = [];
    for (const [formID, formData] of Object.entries(forms)) {
      const formErrors = await this.validateForm(formData);
      if (formErrors.length > 0) {
        errors.push({ formID, errors: formErrors });
      }
    }
    return errors;
  }

  onValidationError(errors: any[]): void {
    console.error('Cannot proceed - form has errors:', errors);
  }

  private async validateForm(formData: any): Promise<any[]> {
    // Simulate async validation
    return [];
  }
}

/**
 * Error Indicators Validation Strategy
 * Allow switching, but show error badges on invalid forms
 */
export class ErrorIndicatorsValidationStrategy implements IValidationStrategy {
  private formErrors = new Map<string, any[]>();

  async canSwitchForm(): Promise<boolean> {
    return true; // Allow switching
  }

  async validateBeforeSave(forms: Record<string, any>): Promise<any[]> {
    const allErrors: any[] = [];
    for (const [formID, formData] of Object.entries(forms)) {
      const errors = await this.validateForm(formData);
      this.formErrors.set(formID, errors);
      if (errors.length > 0) {
        allErrors.push({ formID, errors });
      }
    }
    return allErrors;
  }

  onValidationError(errors: any[]): void {
    // Update UI with error badges
    console.warn('Show error indicators for:', errors);
  }

  getFormErrors(formID: string): any[] {
    return this.formErrors.get(formID) || [];
  }

  private async validateForm(formData: any): Promise<any[]> {
    return [];
  }
}

/**
 * Auto-Save Validation Strategy
 * Validate on change, auto-save valid forms
 */
export class AutoSaveValidationStrategy implements IValidationStrategy {
  private autoSaveTimer: ReturnType<typeof setTimeout> | null = null;
  private autoSaveDelay = 1000; // 1 second

  async canSwitchForm(): Promise<boolean> {
    return true;
  }

  async validateBeforeSave(forms: Record<string, any>): Promise<any[]> {
    const errors: any[] = [];
    for (const [formID, formData] of Object.entries(forms)) {
      const formErrors = await this.validateForm(formData);
      if (formErrors.length > 0) {
        errors.push({ formID, errors: formErrors });
      }
    }
    return errors;
  }

  onValidationError(errors: any[]): void {
    // Schedule auto-save after delay
    if (this.autoSaveTimer) {
      clearTimeout(this.autoSaveTimer);
    }
    this.autoSaveTimer = setTimeout(() => {
      this.performAutoSave();
    }, this.autoSaveDelay);
  }

  private performAutoSave(): void {
    console.log('Performing auto-save of valid data');
  }

  private async validateForm(formData: any): Promise<any[]> {
    return [];
  }
}

/**
 * Validation Strategy Factory
 */
export class ValidationStrategyFactory {
  static createStrategy(
    mode?: 'free' | 'blocked' | 'indicators' | 'auto-save' | null
  ): IValidationStrategy {
    // Default to 'free' if mode is undefined or null
    const resolvedMode = mode || 'free';

    switch (resolvedMode) {
      case 'free':
        return new FreeNavigationValidationStrategy();
      case 'blocked':
        return new BlockedNavigationValidationStrategy();
      case 'indicators':
        return new ErrorIndicatorsValidationStrategy();
      case 'auto-save':
        return new AutoSaveValidationStrategy();
      default:
        throw new Error(`Unknown validation mode: ${resolvedMode}`);
    }
  }
}
